// Boundary sync orchestrator: turns a stored chat into a boundary POST, uploads
// any externalized blobs first, applies the revision guard, and folds the
// outcome back into the outbox. IndexedDB stays the local source of truth in
// front of this layer — a sync failure is never user-blocking.
//
// Called by ChatConsole at the sync boundaries: agent-run end, child-handle
// settle, head-only change, exit flush, and the app-open outbox drain. All work
// is best-effort and gated by the chatPersistenceEnabled() flag; with the flag
// off this module is inert.

import { chatPersistenceEnabled } from "./featureFlags.js";
import { buildChatHead, buildTurns, buildTombstoneTurns, buildTurnHashes, hashTurnPayload, chunkTurnsBySize, headWireBytes, hasSyncableContent, wasInterrupted, TURN_PAYLOAD_SCHEMA_VERSION } from "./chatSyncSerialize.js";
import { extractOversizedLeaves, applyBlobIds, blobAgnosticLeaves, sha256Hex } from "./chatBlobExternalize.js";
import { buildChatCapture } from "./chatExchanges.js";
import { mintServerChatId, chatIdToWireBytes } from "./chatServerId.js";
import {
    postChatSync,
    putChatBlob,
    isRateLimited,
    getChat,
    deleteChat as deleteChatOnServer,
} from "./chatPersistenceApi.js";
import {
    getOutboxEntry,
    putOutboxEntry,
    mergeOutboxEntry,
    loadOutbox,
    initialOutbox,
    markDirty,
    shouldAttempt,
    expectedRevision,
    reconcileAfterAttempt,
    resetForSession,
    retryBackoffMs,
} from "./chatOutbox.js";
import { loadChat, saveChat, deleteChat as deleteChatLocal } from "./chatStorage.js";
import { broadcastChatSynced, broadcastChatDeleted } from "./chatSyncBroadcast.js";
import { fetchServerTranscript, seedRestoredOutbox, hydrateItemsBounded } from "./chatRestore.js";

// A monotonic-enough clock; the outbox only uses timestamps for ordering /
// diagnostics, never correctness.
const now = () => (typeof Date !== "undefined" ? Date.now() : 0);

// Per-localId in-flight sync promises. Two boundaries for the same chat (a
// run-end debounce still awaiting the network when an exit flush or the
// app-open drain fires) would otherwise both POST the same expected_revision —
// the server accepts the first and 409s the second, surfacing a spurious
// "updated elsewhere" banner from a purely local double-send. Serializing per
// chat coalesces the second onto the first.
const _inFlight = new Map();

// Upper bound on the per-chat content-hash dedup cache (2× the server's
// 256-live-blob cap); prune oldest-inserted beyond it so the map can't grow
// unboundedly with a chat's blob churn.
const MAX_BLOB_HASH_ENTRIES = 512;

// Decide whether a save boundary should actually POST a sync, vs just persist
// locally. A real change syncs when idle (run-end / head-only / canvas edit) or
// on exit; an UNCHANGED chat syncs on exit ONLY to record an interruption marker
// (busyAtClose). The load-bearing case: an unchanged, non-interrupted exit flush
// fires on every tab-blur / lock and must NOT POST — it would bump the revision
// for a no-op and risk a spurious cross-device "updated elsewhere — reload" 409.
export function shouldSyncAtBoundary({ chatChanged, busy, exiting, busyAtClose }) {
    return chatChanged ? (!busy || exiting) : (exiting && !!busyAtClose);
}

// Map a sync attempt's result onto the chat-state patch the UI should apply (or
// null for no change), so the conflict / deleted / blocked / ok branching is
// unit-testable out of the component. `chat` supplies the current flags so each
// patch is idempotent; a successful sync clears any prior conflict/blocked flag.
// `kind` disambiguates a terminal "error" status (only "invalid" — an oversize
// chat rejected by the server request cap — is a user-visible permanent block;
// transient network/busy errors just retry and set nothing).
export function syncResultPatch(status, chat = {}, kind = null) {
    switch (status) {
        case "conflict":
            return chat.syncConflict ? null : {
                syncConflict: true,
                // "elsewhere", not "on another device": a second TAB of this
                // browser has its own in-flight lock and can advance the
                // revision just as a second device can.
                error: "This chat was updated elsewhere. Reload to see the latest version.",
            };
        case "deleted":
            return { deletedElsewhere: true, error: "This chat was deleted on another device." };
        case "forbidden":
            // No write access (e.g. Viewer role) — a permanent block for this session.
            return chat.syncBlocked ? null : {
                syncBlocked: true,
                syncBlockedReason: "This chat isn't backing up to your account (no write access).",
            };
        case "error": {
            // Terminal parks that a retry won't fix get a user-visible not-syncing
            // state: "invalid" (oversize chat over the request cap) and "quota"
            // (per-user chat-count cap). Transient error kinds set nothing.
            const reason = kind === "invalid"
                ? "This chat is too large to back up — it stays on this device."
                : kind === "quota"
                    ? "This chat isn't backing up — you've reached your saved-chat limit."
                    : null;
            if (!reason) return null;
            return chat.syncBlocked ? null : { syncBlocked: true, syncBlockedReason: reason };
        }
        case "ok": {
            const patch = {};
            if (chat.syncConflict) { patch.syncConflict = false; patch.error = null; }
            if (chat.syncBlocked) { patch.syncBlocked = false; patch.syncBlockedReason = null; }
            return Object.keys(patch).length ? patch : null;
        }
        default:
            return null;
    }
}

// Plan a cross-tab "deleted" broadcast's effect on the tab strip: given the
// current chats, the local id a sibling tab deleted, and the active id, return
// { chats, activeId, needsFresh }. Pure so the removal / active-reassignment /
// empty-strip decision is unit-testable out of the component; the caller applies
// it inside a functional setChats updater and performs the makeChat side effect
// when `needsFresh` (mirrors closeChat). Returns null if the id isn't present
// (a stale/duplicate broadcast — no-op).
export function planCrossTabDelete(chats, localId, activeId) {
    const idx = chats.findIndex((c) => c.id === localId);
    if (idx < 0) return null;
    const next = chats.slice(0, idx).concat(chats.slice(idx + 1));
    if (next.length === 0) return { chats: null, activeId, needsFresh: true };
    // If the deleted tab was active, jump to the neighbor that took its slot
    // (or the new last tab when the rightmost was deleted).
    const nextActive = activeId === localId ? (next[Math.min(idx, next.length - 1)]?.id ?? activeId) : activeId;
    return { chats: next, activeId: nextActive, needsFresh: false };
}

// Plan the outbox effects for one save boundary, so the component wiring is a
// trivial `if (plan.x) effect()` over a unit-tested decision. Returns
// { markDirty, sync, interrupted }: `markDirty` records the chat dirty in the
// outbox (cheap, no network); `sync` fires an actual boundary POST. Both are
// gated on persistence being on, the chat having a server row, and there being
// something to persist (a change, or an exit flush). `sync` additionally
// defers to shouldSyncAtBoundary so an unchanged non-interrupted exit flush
// never re-POSTs.
export function planSaveBoundarySync({ payload, chatChanged, exiting, busy, persistenceOn }) {
    if (!persistenceOn || !payload?.serverChatId || !(chatChanged || exiting)) {
        return { markDirty: false, sync: false, interrupted: false };
    }
    const busyAtClose = !!payload.busyAtClose;
    return {
        markDirty: !!chatChanged,
        sync: shouldSyncAtBoundary({ chatChanged, busy, exiting, busyAtClose }),
        // Derived, not just `busyAtClose`: the flag is transient and a reopened
        // tab has already lost it (see wasInterrupted).
        interrupted: wasInterrupted(payload),
    };
}

// Plan a chat close: when persistence is on and the chat has a server row,
// closing it is a real cross-device delete (tombstone turns + blobs); otherwise
// it's a local-only IDB delete. Pure so the branch is unit-testable out of the
// component.
export function planChatClose({ chatObj, persistenceOn }) {
    const serverChatId = chatObj?.serverChatId || null;
    return persistenceOn && serverChatId
        ? { mode: "everywhere", serverChatId }
        : { mode: "local", serverChatId: null };
}

// Classify a cross-tab chat-sync broadcast against the current chats: ignore it
// (no server id, or no matching local tab), or route a "deleted" / "synced"
// event to a known local chat. Pure so the dispatch guard is unit-testable; the
// caller performs the setChats / delete side effects.
export function classifyChatSyncMessage(msg, chats) {
    if (!msg?.serverChatId) return { kind: "ignore" };
    const local = (chats || []).find((c) => c.serverChatId === msg.serverChatId);
    if (!local) return { kind: "ignore" };
    if (msg.type === "deleted") return { kind: "delete", local };
    if (msg.type === "synced") return { kind: "synced", local };
    return { kind: "ignore" };
}

// Decide how to resolve a conflicted chat once the server copy is in hand.
// Returns true ("keep local") when the local body is the server transcript's
// prefix plus extra unsynced turns — the lost-ack case where a backup committed,
// its ack died, and the user kept typing: those extra turns must be re-pushed,
// NOT discarded by a wholesale adopt-server. Returns false ("adopt server",
// last-write-wins) when there's no local body or any shared-ordinal turn differs
// (genuinely divergent — another writer changed the shared history). Uses the
// same buildTurnHashes fingerprints the delta sync keys on, so "matches" here
// means "the delta sync would send nothing for that ordinal".
export function planConflictKeepLocal(localBody, serverPayload) {
    if (!localBody) return false;
    // Hash blob-agnostically: the local IDB body stores oversized leaves inline
    // while the server copy stores them as placeholders, so a raw buildTurnHashes
    // would diverge on every heavy turn and wrongly adopt-server (data loss). The
    // sentinel projection makes the two storage forms of the same content equal.
    const hashesOf = (body) => {
        const out = {};
        for (const t of buildTurns(body)) out[t.turn_ordinal] = hashTurnPayload(blobAgnosticLeaves(t.payload));
        return out;
    };
    const localHashes = hashesOf(localBody);
    const serverHashes = hashesOf(serverPayload);
    const serverOrdinals = Object.keys(serverHashes);
    // Every server turn must match local at the same ordinal (server is a prefix
    // of local); a mismatch means the shared history diverged → adopt server.
    for (const o of serverOrdinals) {
        if (localHashes[o] !== serverHashes[o]) return false;
    }
    // ...and local must carry at least as many turns (the prefix, plus any
    // local-only tail to push). Fewer means local rewound below the server →
    // divergent, adopt server.
    return Object.keys(localHashes).length >= serverOrdinals.length;
}

// Assemble the SyncChat request body (proto JSON). Pure so it's unit-testable.
// `chat_id` is base64 `bytes` (the RPC form, NOT the hyphenated blob-route form);
// head fields are FLAT; each turn's render document + blob refs travel as JSON
// strings in `payload_json` / `blob_refs_json` with a `payload_schema_version`.
export function buildSyncBody({ serverChatId, expectedRevision: rev, head, turns, conversations, deferredExchanges, syncNonce }) {
    const body = {
        // Spread head first so the fixed protocol fields always win, even if the
        // head key set ever grows to include one of these names.
        ...head,
        chat_id: chatIdToWireBytes(serverChatId),
        expected_revision: rev,
        turns: (turns || []).map((t) => ({
            turn_ordinal: t.turn_ordinal,
            payload_json: JSON.stringify(t.payload ?? {}),
            payload_schema_version: TURN_PAYLOAD_SCHEMA_VERSION,
            blob_refs_json: JSON.stringify(t.blob_refs ?? []),
        })),
    };
    // Captured conversations are already in their proto shape (chatExchanges.js
    // builds `messages_json`, the prefix counts and the flat token fields), so
    // they ride as-is.
    if (conversations?.length) body.conversations = conversations;
    // Backlog signal, not content: how many more the budget held back. Server
    // meters it so partial capture of a long run is visible.
    if (deferredExchanges > 0) body.deferred_exchange_count = deferredExchanges;
    // Idempotent-retry nonce (proto `bytes` → base64). Omitted → the server
    // gives a plain 409 on a revision-CAS miss (still handled, just not idempotent).
    if (syncNonce) body.sync_nonce = syncNonce;
    return body;
}

// A fresh 16-random-byte idempotency nonce as base64 (the proto `bytes` form).
export function newSyncNonce() {
    const bytes = new Uint8Array(16);
    const c = typeof crypto !== "undefined" ? crypto : (globalThis.crypto ?? null);
    if (c?.getRandomValues) c.getRandomValues(bytes);
    else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256); // non-crypto fallback; nonce only gates idempotency, not security
    let s = "";
    for (const b of bytes) s += String.fromCharCode(b);
    return btoa(s);
}

// Decode one externalized leaf into the bytes to PUT + its true byte size. A
// data-URL uploads its decoded binary under the real mime; text uploads UTF-8.
function encodeBlobBody(blob) {
    if (blob.encoding === "data-url") {
        const m = /^data:[^;,]+;base64,(.*)$/s.exec(blob.data);
        const b64 = m ? m[1] : "";
        const binary = safeAtob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return { body: bytes, contentType: blob.contentType, sizeBytes: bytes.length };
    }
    const enc = typeof TextEncoder !== "undefined" ? new TextEncoder().encode(blob.data) : strBytes(blob.data);
    return { body: enc, contentType: blob.contentType, sizeBytes: enc.length };
}

function safeAtob(b64) {
    try { return atob(b64); } catch { return ""; }
}
function strBytes(s) {
    const out = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff;
    return out;
}

// Drop any dedup entries whose blobId the server just evicted, so evicted
// content re-uploads next time it's referenced rather than resolving to a
// gone object. Exported for unit testing.
export function pruneEvicted(blobHashes, evictedBlobIds) {
    if (!evictedBlobIds?.length) return;
    const gone = new Set(evictedBlobIds);
    for (const [hash, meta] of Object.entries(blobHashes)) {
        if (gone.has(meta?.blobId)) delete blobHashes[hash];
    }
}

// Bound the dedup cache: drop oldest-inserted entries past the cap. Exported
// for unit testing.
export function capBlobHashes(blobHashes) {
    const keys = Object.keys(blobHashes);
    if (keys.length <= MAX_BLOB_HASH_ENTRIES) return;
    for (const hash of keys.slice(0, keys.length - MAX_BLOB_HASH_ENTRIES)) {
        delete blobHashes[hash];
    }
}

// Seed the per-chat dedup cache with blobs this device just DOWNLOADED, so a
// restored chat doesn't re-upload content that is already stored.
//
// Restore is read-only, but hydration is not inert: it replaces each placeholder
// with the inline content in React state, which flips the save fingerprint, and
// the resulting boundary re-serializes the turn. The externalizer then sees an
// oversized leaf again and — with nothing in the cache, because the placeholder's
// blob id was discarded at hydration and `seedRestoredOutbox` has no content to
// hash — mints a NEW id and PUTs a duplicate. The server dedups on blob id
// alone, so a duplicate is a real second row: it consumes the per-chat live-blob
// budget and pushes the original (the row every other device's blob_refs point
// at) toward oldest-first eviction. Opening a chat on a second device therefore
// degraded the copy on the first.
//
// Keyed on content hash, so the reuse path is the same one an unchanged local
// leaf takes, and `applyBlobIds` re-emits the ORIGINAL blob id — no new row, and
// the turn's blob_refs keep pointing where they already did.
//
// All hashing happens BEFORE the row is touched, so the merge itself is
// synchronous and can run inside one IndexedDB transaction (mergeOutboxEntry) —
// a plain get-then-put would let two restores of the same chat both read the
// pre-merge row and drop the loser's entries.
export async function recordHydratedBlobs({ tenantId, localId, blobs }) {
    if (!chatPersistenceEnabled() || !blobs?.length) return 0;
    const additions = {};
    for (const blob of blobs) {
        if (!blob?.blobId || typeof blob.content !== "string") continue;
        const hash = await sha256Hex(blob.content);
        if (!hash) continue;
        additions[hash] = {
            blobId: blob.blobId,
            bytes: blob.bytes ?? 0,
            contentType: blob.contentType || "application/octet-stream",
        };
    }
    const added = Object.keys(additions).length;
    if (!added) return 0;
    const merged = await mergeOutboxEntry(tenantId, localId, (entry) => {
        // No row means no chat to dedup against (never synced, or deleted while
        // the blobs were downloading) — creating one here would resurrect it.
        if (!entry) return null;
        const blobHashes = { ...(entry.blobHashes || {}), ...additions };
        capBlobHashes(blobHashes);
        return { ...entry, blobHashes };
    });
    return merged ? added : 0;
}

// Hydrate a restored chat's blob placeholders AND record what came back, in the
// order those two have to happen: the caller applies the hydrated items to its
// state next, and that is what flips the save fingerprint and schedules the
// boundary which would otherwise re-upload every blob just downloaded.
//
// Recording is bookkeeping, so its failure is swallowed here rather than by the
// caller's handler: letting it cost the user the hydrated transcript would trade
// a redundant upload for a visibly broken chat.
export async function hydrateRestoredBlobs({ tenantId, localId, serverChatId, items, concurrency = 4 }) {
    const downloaded = [];
    const hydratedByOriginal = await hydrateItemsBounded(serverChatId, items, concurrency, {
        onHydrated: (blob) => downloaded.push(blob),
    });
    try {
        await recordHydratedBlobs({ tenantId, localId, blobs: downloaded });
    } catch (err) {
        console.warn("[chatSync] recording hydrated blobs failed; they may re-upload:", err?.message || err);
    }
    return hydratedByOriginal;
}

// Upload the blobs for one turn (client mints the id → single PUT), tolerating
// the per-(tenant,user) blob-upload rate limit: a 429 (or any transient) defers
// the remaining blobs to the next boundary rather than hot-retrying. A leaf whose
// content hash is already in `blobHashes` reuses the stored blob with no
// network. Mutates `blobHashes` (adds stored, prunes evicted). Returns
// { refToBlob, deferred } — deferred means at least one blob didn't land, so
// the chat stays dirty.
async function uploadTurnBlobs(serverChatId, turnOrdinal, blobs, blobHashes) {
    const refToBlob = {};
    let deferred = false;
    let evicted = false;
    for (const blob of blobs) {
        const hash = await sha256Hex(blob.data);
        if (hash && blobHashes[hash]) {
            // Unchanged content already stored on the server — reuse it.
            refToBlob[blob.ref] = blobHashes[hash];
            continue;
        }
        try {
            const { body, contentType, sizeBytes } = encodeBlobBody(blob);
            // Client mints the blob id (hyphenated UUID); one PUT stores the
            // bytes inline in Postgres and returns the eviction list.
            const blobId = mintServerChatId();
            const put = await putChatBlob({ chatId: serverChatId, blobId, turnOrdinal, contentType, bytes: body });
            const meta = { blobId, bytes: sizeBytes, contentType };
            refToBlob[blob.ref] = meta;
            if (hash) blobHashes[hash] = meta;
            if (put.evictedBlobIds?.length) evicted = true;
            pruneEvicted(blobHashes, put.evictedBlobIds);
        } catch (err) {
            // Transient failure (rate-limit / network / 5xx) → defer the rest so
            // the outbox retries next boundary; putChatBlob now stamps `.status`
            // on every HTTP error, so a 5xx must be caught here explicitly rather
            // than falling through to the terminal skip below (else a blob lost
            // to a rolling deploy would never re-upload and restore cross-device
            // as "content unavailable").
            if (isRateLimited(err) || err?.kind === "network" || !err?.status || err.status >= 500) {
                deferred = true;
                break;
            }
            // Terminal 4xx (forbidden / bad request): skip this blob (leaves an
            // unresolved null marker) and continue with the others — a retry
            // won't fix it.
        }
    }
    return { refToBlob, deferred, evicted };
}

// Externalize + upload blobs across all turns, returning turns with resolved
// blob_refs and whether any blob was deferred. `blobHashes` (the per-chat
// dedup cache) is mutated in place and persisted by the caller.
async function externalizeTurns(serverChatId, turns, blobHashes) {
    let anyDeferred = false;
    let anyEvicted = false;
    const out = [];
    for (const turn of turns) {
        const { payload, blobs } = extractOversizedLeaves(turn.payload);
        if (blobs.length === 0) {
            out.push({ turn_ordinal: turn.turn_ordinal, payload, blob_refs: [] });
            continue;
        }
        const { refToBlob, deferred, evicted } = await uploadTurnBlobs(serverChatId, turn.turn_ordinal, blobs, blobHashes);
        if (deferred) anyDeferred = true;
        if (evicted) anyEvicted = true;
        const { payload: finalPayload, blobRefs } = applyBlobIds(payload, refToBlob);
        out.push({ turn_ordinal: turn.turn_ordinal, payload: finalPayload, blob_refs: blobRefs });
    }
    capBlobHashes(blobHashes);
    return { turns: out, anyDeferred, anyEvicted };
}

// Record that a chat has unsynced local changes WITHOUT attempting a network
// sync. Called on every IDB save so a run that is killed mid-stream (before any
// boundary sync fires) still leaves a dirty outbox entry — the next app-open
// drain then pushes it. Cheap: one IDB write, no network.
export async function markChatDirty({ tenantId, localId, serverChatId }) {
    if (!chatPersistenceEnabled() || !serverChatId) return;
    const existing = await getOutboxEntry(tenantId, localId);
    // A parked (conflict / terminal) entry keeps its park; markDirty only lifts
    // a forbidden park (a fresh edit is a fresh reason to try) and never a
    // conflict or deleted tombstone.
    const entry = existing
        ? markDirty({ ...existing, serverChatId }, { now: now() })
        : initialOutbox({ serverChatId, now: now() });
    await putOutboxEntry(tenantId, localId, entry);
}

// Sync one chat from its stored IDB payload. `storedPayload` is the chat record
// shape ({ items, title, kind, thinkingLevel, scopeMode, toggled, canvas,
// serverChatId }). Returns a result the caller can act on:
//   { status: "ok"|"skipped"|"conflict"|"deleted"|"forbidden"|"error", ... }
//
// Serialized per (tenant, localId): a second boundary for the same chat waits
// for the in-flight one and then re-reads the outbox, so overlapping
// flush/exit/drain calls can't POST the same expected_revision and self-409.
export function syncStoredChat(args) {
    const key = `${args.tenantId}:${args.localId}`;
    const prior = _inFlight.get(key) || Promise.resolve();
    const result = prior.then(() => syncStoredChatInner(args), () => syncStoredChatInner(args));
    // Store a rejection-swallowing tail so the chain never surfaces a prior
    // failure into the next queued boundary.
    const tail = result.then(() => {}, () => {});
    _inFlight.set(key, tail);
    // Evict once settled so the map doesn't retain one dead entry per chat
    // touched this session — but only if no later boundary has since queued
    // behind this tail (that would have replaced the entry; its own finally
    // cleans up).
    tail.finally(() => { if (_inFlight.get(key) === tail) _inFlight.delete(key); });
    return result;
}

async function syncStoredChatInner(args) {
    const { tenantId, localId, serverChatId, storedPayload, interrupted, exchangeSource, force = false, conflictRetried = false } = args;
    if (!chatPersistenceEnabled()) return { status: "skipped", reason: "flag-off" };
    if (!serverChatId) return { status: "skipped", reason: "no-server-id" };

    const existing = await getOutboxEntry(tenantId, localId);
    let entry = existing ? markDirty(existing, { now: now() }) : initialOutbox({ serverChatId, now: now() });
    entry.serverChatId = serverChatId;
    // Never CREATE a server record for a chat with no user content. A blank
    // tab mints a server id at creation and its first debounced boundary
    // reports "changed" (no prior fingerprint on disk), which used to POST a
    // head-only create and leave an empty row on the server forever — one per
    // blank tab, per user. Keyed on ackedRevision 0 (no server row yet), so an
    // EXISTING chat rewound to zero items still syncs its tombstones. Returns
    // without persisting the outbox entry so a blank tab causes no IDB churn.
    if ((entry.ackedRevision ?? 0) === 0 && !hasSyncableContent(storedPayload)) {
        return { status: "skipped", reason: "empty-chat" };
    }
    // A 409 parks the chat, and until now only a page reload lifted the park -
    // resolveConflicts runs once, at mount. So a chat that conflicted mid-session
    // stopped backing up for the REST of that session while the user kept typing
    // into it, and the turns only landed after the next reload. Retry the
    // lost-ack half of that decision here instead, at the boundary that would
    // otherwise skip.
    //
    // Probed at most once per parked revision: on genuine divergence the resolve
    // declines (adoption is reload-only), and without the guard every subsequent
    // boundary would re-fetch the whole server transcript for a chat that is
    // going to stay parked. A fresh 409 adopts the server's revision, which
    // re-arms the probe.
    if (entry.conflict && entry.conflictProbedRevision !== entry.ackedRevision) {
        entry.conflictProbedRevision = entry.ackedRevision;
        await putOutboxEntry(tenantId, localId, entry);
        const recovered = await resolveConflictedChat(tenantId, localId, entry, { allowAdoptServer: false });
        if (recovered) entry = { ...recovered, conflictProbedRevision: recovered.ackedRevision };
    }
    if (!force && !shouldAttempt(entry, { now: now() })) {
        await putOutboxEntry(tenantId, localId, entry);
        const reason = entry.conflict
            ? "conflict-parked"
            : entry.terminalKind || (entry.dirty && entry.nextAttemptAt > now() ? "backoff" : "not-dirty");
        return { status: "skipped", reason };
    }

    const head = buildChatHead(storedPayload, { interrupted });
    // Wire exchanges since the last ack. Built here rather than by the caller
    // because the cursor lives on the outbox entry alongside `turnHashes` — the
    // two deltas advance under the same ack.
    const {
        conversations,
        cursors: nextExchangeCursors,
        bytes: captureBytes,
        deferred: deferredExchanges,
    } = exchangeSource
        ? buildChatCapture({
            ...exchangeSource,
            cursors: entry.exchangeCursors || {},
            capturedThrough: entry.capturedThrough || 0,
        })
        : { conversations: [], cursors: entry.exchangeCursors || {}, bytes: 0, deferred: 0 };
    const realTurns = buildTurns(storedPayload);
    // Delta sync: POST only turns whose content changed since the last ack. Each
    // turn is upsertable at its ordinal, so re-sending an unchanged turn is pure
    // write-amplification (server upsert + revision churn ∝ retained history).
    // `turnHashes` is the per-ordinal fingerprint the last successful sync
    // recorded; a turn missing from it (new, or first sync) counts as changed.
    const prevHashes = entry.turnHashes || {};
    const nextHashes = {};
    const changedTurns = [];
    for (const t of realTurns) {
        const h = hashTurnPayload(t.payload);
        nextHashes[t.turn_ordinal] = h;
        if (prevHashes[t.turn_ordinal] !== h) changedTurns.push(t);
    }
    // Rewind handling: if the chat shrank below what the server last acked
    // (editAndRerun / rewindToUser / rewindToTurn slice items[]), the sync
    // route's upsert-only writes would leave the vacated ordinals live and
    // resurrect them on restore. Overwrite the newly-vacated ordinals with
    // tombstones the restore read drops. `tombstonedThrough` records the floor
    // of the tail already tombstoned-and-acked, so a chat that stays shrunk
    // tombstones each ordinal once rather than re-sending the whole dead tail
    // every boundary. Its absence (never synced / freshly restored) means
    // "nothing known dead yet" → fall back to the high-water so the first shrink
    // truncates the full tail.
    const deadCeil = entry.ackedTurnCount ?? 0;
    const tombFrom = entry.tombstonedThrough ?? deadCeil;
    const tombHi = Math.min(tombFrom, deadCeil); // exclusive upper bound of NEW tombstones
    const tombstones = realTurns.length < tombHi
        ? buildTombstoneTurns(realTurns.length, tombHi - 1)
        : [];
    const allTurns = [...changedTurns, ...tombstones];
    const blobHashes = { ...(entry.blobHashes || {}) };
    // Chunk the changed turns + tombstones into POSTs bounded by BOTH the turn
    // count and a byte budget (chunkTurnsBySize), so a run of heavy turns can't
    // bundle past the server's request cap and park the chat. The head rides
    // only the first POST; each POST advances the revision the next one guards
    // on. At least one POST always fires (a head-only / tombstone-only boundary
    // is legitimate).
    // Exchanges ride POST #0 with the head, so their wire bytes come out of the
    // FIRST batch's turn budget — otherwise a heavy boundary could pack 7 MiB
    // of turns beside 3 MiB of exchanges and blow the gateway's 8 MiB
    // decompressed cap, parking the chat as permanently-invalid.
    const firstBatchReserve = headWireBytes(head) + captureBytes;
    const batches = allTurns.length
        ? chunkTurnsBySize(allTurns, undefined, undefined, firstBatchReserve)
        : [[]];

    // Idempotent-retry nonce, stable across retries of THIS logical boundary
    // POST. If a sync commits but its ack is lost, the retry sends a now-stale
    // expected_revision; a matching nonce lets the server ACK its own committed
    // write idempotently instead of a spurious 409 the client can't tell from a
    // real concurrent write. A signature over the exact bytes we're about to send
    // gates reuse: ANY change to the head, the changed turns, the tombstones, the
    // pushed exchanges, or expected_revision mints a FRESH nonce — so a nonce can
    // never let the server short-circuit a request whose body differs from the
    // committed one (which would silently drop the new content). Exchanges are in
    // the signature for exactly that reason: a retry that has picked up a newly
    // completed LLM call must not be ACK'd as a replay of the call-less commit,
    // because the ack is what advances the capture cursor past them.
    // Reused across this attempt's batches;
    // cleared on full success so the next logical sync starts fresh.
    //
    // Scope: idempotent lost-ack recovery covers SINGLE-batch syncs (the common
    // case, ≤ MAX_TURNS_PER_SYNC changed turns). For a multi-batch sync a lost ack
    // on a non-first batch shrinks changedTurns and advances the revision on the
    // retry, so the signature differs and a fresh nonce is minted — that batch
    // then takes an ordinary 409, which resolveConflicts reconciles by reload
    // with no turn loss. Safe degradation (a spurious reload), not data loss, and
    // minting fresh is the correct choice since the retry's body genuinely differs.
    const nonceSig = hashTurnPayload({
        r: expectedRevision(entry),
        h: head,
        c: changedTurns.map((t) => [t.turn_ordinal, nextHashes[t.turn_ordinal]]),
        d: tombstones.map((t) => t.turn_ordinal),
        x: conversations.map((c) => [c.conversation, c.exchanges.map((e) => e.exchange_id)]),
    });
    if (!entry.syncNonce || entry.syncNonceSig !== nonceSig) {
        entry.syncNonce = newSyncNonce();
        entry.syncNonceSig = nonceSig;
    }
    const syncNonce = entry.syncNonce;

    let ack = null;
    let anyDeferred = false;
    let anyEvicted = false;
    // Snapshot the retry count BEFORE the loop: a successful batch POST's
    // ok-reconcile zeroes entry.attempts, so the deferred-blob backoff below must
    // escalate from this pre-loop value (else it would pin flat at one step
    // forever under sustained rate-limiting).
    const priorAttempts = entry.attempts || 0;
    // Track hashes acked so far so a mid-way POST failure still records which
    // ordinals landed (a retry then re-sends only the rest). Starts from the
    // prior fingerprints — unchanged turns keep theirs.
    const pendingHashes = { ...prevHashes };
    for (let i = 0; i < batches.length; i++) {
        const { turns, anyDeferred: deferred, anyEvicted: evicted } = await externalizeTurns(serverChatId, batches[i], blobHashes);
        if (deferred) anyDeferred = true;
        if (evicted) anyEvicted = true;
        // Persist the dedup cache regardless of the POST outcome — the blobs are
        // already stored on the server, so a later re-sync must not re-upload
        // them even if this POST fails.
        entry.blobHashes = blobHashes;
        const body = buildSyncBody({
            serverChatId,
            expectedRevision: expectedRevision(entry),
            head: i === 0 ? head : {},
            turns,
            // Capture rides the first POST only: publication is per-boundary,
            // not per-chunk, and re-sending the transcripts on every chunk
            // would reintroduce exactly the duplication this shape removes.
            conversations: i === 0 ? conversations : undefined,
            deferredExchanges: i === 0 ? deferredExchanges : 0,
            syncNonce,
        });
        try {
            ack = await postChatSync(body);
            entry = reconcileAfterAttempt(entry, { ok: true, revision: ack.revision }, { now: now() });
            entry.ackedRevision = ack.revision;
            // Advance the capture cursor as soon as the carrying POST is acked.
            // Publication is fail-open server-side, so an ack does NOT prove the
            // events reached Kafka, and these exchanges are never re-sent — the
            // ack is the only signal we get. Accepted deliberately: the server
            // cannot report a publish that outlives the response, and holding
            // the cursor back would re-send the same calls on every subsequent
            // boundary forever (their ids are deterministic, so the successful
            // ones would just republish identically). The conversation still
            // reaches the analytics store on the chat's next call, whose request
            // carries the full prior history.
            if (i === 0 && conversations.length) entry.exchangeCursors = nextExchangeCursors;
            for (const t of batches[i]) {
                if (t.turn_ordinal < realTurns.length) pendingHashes[t.turn_ordinal] = nextHashes[t.turn_ordinal];
                else delete pendingHashes[t.turn_ordinal]; // a tombstoned ordinal is no longer live
            }
        } catch (err) {
            // If a blob eviction/deferral already happened earlier this sync,
            // clear the fingerprints so the retry re-sends every turn and
            // re-uploads the missing content — the POST failed, but the blob
            // outcome is a separate server operation (eviction pruned the dedup
            // cache; a deferred blob was left a null marker).
            entry.turnHashes = (anyEvicted || anyDeferred) ? {} : pendingHashes;
            const failure = await persistSyncFailure(entry, err, tenantId, localId, serverChatId);
            if (failure.status !== "conflict" || conflictRetried) return failure;
            return await recoverLostAck(args, failure);
        }
    }

    entry.blobHashes = blobHashes;
    // Record the server's turn_count (MAX ordinal+1, tombstones included) as the
    // high-water, and advance the dead-tail floor to the live turn count so the
    // next boundary re-tombstones only ordinals a further shrink vacates.
    entry.ackedTurnCount = ack.turn_count ?? entry.ackedTurnCount ?? 0;
    entry.tombstonedThrough = Math.min(realTurns.length, entry.ackedTurnCount);
    // On full success the live turns are exactly nextHashes (no stale tail keys).
    // But if a blob was EVICTED (pruned from the dedup cache) or DEFERRED (POSTed
    // as a null marker because its PUT was rate-limited), some turn's content is
    // not fully on the server; clear the fingerprints + keep the chat dirty so
    // the next boundary re-sends every turn and re-uploads the missing blobs.
    // Delta would otherwise treat the turn as unchanged (its payload hash is
    // stable) and never retry, stranding the blob as "content unavailable"
    // cross-device. Known limitation: a chat that stays above the server's
    // live-blob cap evicts every boundary, degrading to a full re-send each time
    // (delta + dedup stop helping) — bounded work, no convergence until it drops
    // back under the cap.
    if (anyEvicted || anyDeferred) {
        entry.turnHashes = {};
        entry = markDirty(entry, { now: now() });
        if (anyDeferred) {
            // The deferred blob PUT was rate-limited (429). Back off before the
            // retry — a successful sync's ok-reconcile zeroed the backoff clock,
            // so without this the next boundary immediately re-hits the limiter
            // that just asked us to wait. Escalate from priorAttempts (the ack
            // reset entry.attempts to 0) so consecutive deferring boundaries walk
            // the same 2s→…→5min ladder the catch path uses, rather than pinning
            // flat and re-POSTing the whole transcript every 2s under a sustained
            // rate-limit (which would amplify, not relieve, the incident).
            const attempts = priorAttempts + 1;
            entry.attempts = attempts;
            entry.nextAttemptAt = now() + retryBackoffMs(attempts);
        }
    } else {
        entry.turnHashes = nextHashes;
    }
    // Every POST acked, so this nonce has served its purpose — retire it. Even
    // the deferred/evicted re-send (which clears turnHashes and advances the
    // revision) would compute a different signature next attempt and mint a fresh
    // nonce anyway, so clearing here unconditionally keeps the stored state honest.
    entry.syncNonce = null;
    entry.syncNonceSig = null;
    await putOutboxEntry(tenantId, localId, entry);
    broadcastChatSynced({ serverChatId, localId, revision: ack.revision });
    return {
        status: "ok",
        revision: ack.revision,
        turnCount: ack.turn_count,
        truncatedBeforeOrdinal: ack.truncated_before_ordinal ?? null,
        deferredBlobs: anyDeferred,
    };
}

// Fold a failed sync POST into the outbox and return the caller-facing result.
async function persistSyncFailure(entry, err, tenantId, localId, serverChatId) {
    let kind = err?.kind || "network";
    if (kind === "precondition") {
        // HTTP 400: a tombstoned chat (gRPC FailedPrecondition) and a malformed
        // body (InvalidArgument) are indistinguishable on the wire (no structured
        // error detail). Reload GetChat and key on the deletedAt tombstone rather
        // than the error string — a delete is destructive (drops the local body),
        // so we confirm it. If GetChat can't be reached we can't confirm a delete,
        // so fall back to the non-destructive "invalid" park; reconcileChatList
        // drops the chat later if it really was tombstoned.
        let deleted = false;
        try {
            const head = serverChatId ? await getChat(serverChatId) : null;
            deleted = !!head?.deletedAt;
        } catch { /* unreachable → treat as invalid (local-only), not a delete */ }
        kind = deleted ? "deleted" : "invalid";
    }
    entry = reconcileAfterAttempt(entry, { ok: false, kind, currentRevision: err?.currentRevision }, { now: now() });
    if (kind === "deleted") {
        // Tombstoned elsewhere → drop the local body, but LEAVE a terminal
        // 'deleted' outbox marker rather than deleting the row: a second
        // boundary already queued on the in-flight lock would otherwise re-read
        // an absent entry, fall to initialOutbox, and POST a fresh create
        // (expected_revision 0) over the tombstone. deleteChatLocal clears the
        // chatSync row, so re-put the marker after it; shouldAttempt then
        // short-circuits the queued call.
        await deleteChatLocal(tenantId, localId);
        await putOutboxEntry(tenantId, localId, entry);
        return { status: "deleted" };
    }
    await putOutboxEntry(tenantId, localId, entry);
    if (kind === "conflict") return { status: "conflict", currentRevision: err?.currentRevision ?? null };
    if (kind === "forbidden") return { status: "forbidden" };
    return { status: "error", kind };
}

// A 409 is not evidence that anyone else touched the chat, and the banner it
// used to raise said otherwise.
//
// The ordinary cause is this device's OWN lost ack: the exit flush POSTs during
// `beforeunload`, the server commits, and the response dies with the page — so
// the revision advanced with nobody left to record it. The idempotency nonce
// only rescues a byte-identical retry, and by the next boundary the user has
// typed something, so the retry is a genuine compare-and-swap miss. The chat
// then displayed "updated on another device" to the one person who had been
// using it, on one device.
//
// Resolve it here, before the caller renders anything: fetch the server copy and
// ask the SAME question the mount path asks (planConflictKeepLocal — is the
// server transcript a prefix of ours?). A prefix means we are looking at our own
// committed write, so realign to its revision and push the tail immediately, at
// most once (`conflictRetried`). Genuine divergence still parks and still tells
// the user, because there the reload is the real answer.
// Recovery is best-effort: the chat is already parked and the caller already has
// an honest answer, so anything that goes wrong here degrades to that answer
// rather than turning a handled 409 into a rejected boundary.
async function recoverLostAck(args, failure) {
    const { tenantId, localId } = args;
    try {
        const parked = await getOutboxEntry(tenantId, localId);
        if (!parked?.conflict) return failure;
        // Bound the transcript fetch exactly as the boundary-entry probe does:
        // once per parked revision. A park this declines to lift (divergence,
        // which only a reload adopts) must not re-fetch at every later boundary.
        const probed = { ...parked, conflictProbedRevision: parked.ackedRevision };
        await putOutboxEntry(tenantId, localId, probed);
        const realigned = await resolveConflictedChat(tenantId, localId, probed, { allowAdoptServer: false });
        if (!realigned) return failure;
        return await syncStoredChatInner({ ...args, conflictRetried: true });
    } catch {
        return failure;
    }
}

// The local turns a divergent server copy would otherwise destroy: everything
// from the first ordinal whose content differs onward.
//
// Conflicts are decided by POSITION, so two devices that merely APPENDED in
// parallel diverge at the first new ordinal - nothing contradicts, both simply
// added - and "is local a prefix of the server" answers no. Replacing the local
// body there destroyed those turns in IDB with no trace and no recovery. For a
// chat open on a laptop and a desktop that is the ordinary case, not an exotic
// one, and it was observed live: a message typed in the stale window vanished on
// the next reload.
//
// Compared blob-agnostically for the same reason planConflictKeepLocal is: local
// stores oversized leaves inline where the server stores placeholders, so a raw
// compare would call every heavy turn divergent and drag the split earlier than
// it belongs. Pure + exported for unit testing.
export function divergentLocalTail(localBody, serverPayload) {
    const localItems = Array.isArray(localBody?.items) ? localBody.items : [];
    if (!localItems.length) return [];
    const fingerprints = (body) =>
        buildTurns(body).map((t) => hashTurnPayload(blobAgnosticLeaves(t.payload)));
    const local = fingerprints(localBody);
    const server = fingerprints(serverPayload);
    let shared = 0;
    while (shared < local.length && shared < server.length && local[shared] === server[shared]) {
        shared++;
    }
    return localItems.slice(shared);
}

// Renumber merged-in turns so they cannot collide with the transcript they are
// appended to.
//
// A turn's id is POSITIONAL - `applyAgentEvent` mints `turn:<count of turns so
// far>` - so two devices appending in parallel mint the very same id. That was
// invisible while a divergent tail was discarded; preserving it puts both in one
// transcript. The id is what the expand/collapse map (`item_ui_state`) keys on,
// so a collision makes two unrelated turns toggle as one.
//
// Continuing the numbering from the adopted transcript restores the positional
// invariant rather than inventing a new id shape, and the `taken` guard keeps it
// correct even if the server's own numbering has gaps. Non-turn items carry no
// such id and pass through. The tail's own expand/collapse state does not
// survive, which costs nothing: a merge adopts the server's `toggled` map
// wholesale anyway. Pure + exported for unit testing.
export function reidMergedTail(tail, serverItems) {
    const taken = new Set(serverItems.map((item) => item?.id).filter(Boolean));
    let next = serverItems.filter((item) => item?.kind === "turn").length;
    return tail.map((item) => {
        if (item?.kind !== "turn") return item;
        while (taken.has(`turn:${next}`)) next++;
        const id = `turn:${next++}`;
        taken.add(id);
        return { ...item, id };
    });
}

// Align the outbox to the server's revision while KEEPING `body` as the local
// truth: stay dirty so the next attempt pushes whatever `body` holds beyond the
// server, with turnHashes seeded from the server prefix so the delta sends
// exactly that and nothing else.
async function realignToServer(tenantId, localId, entry, body, fetched) {
    const cur = await getOutboxEntry(tenantId, localId);
    const next = {
        ...(cur || entry),
        serverChatId: entry.serverChatId,
        conflict: false,
        dirty: true,
        lastError: null,
        ackedRevision: fetched.head.revision,
        ackedTurnCount: fetched.head.turnCount ?? Object.keys(buildTurnHashes(fetched.payload)).length,
        tombstonedThrough: (body.items || []).length,
        turnHashes: buildTurnHashes(fetched.payload),
        updatedAt: now(),
    };
    await putOutboxEntry(tenantId, localId, next);
    return next;
}

// Resolve ONE conflict-parked chat against the server copy. Fetch the server
// transcript, then decide (planConflictKeepLocal):
//   - keep-local: the local body is the server prefix plus turns typed after a
//     lost-ack conflict — preserve the local body and re-push the extra tail at
//     the server's current revision (do NOT overwrite; that would delete the
//     user's recent work, the pre-fix behavior).
//   - divergent: the shared history genuinely differs. Adopt the server
//     transcript but RE-APPEND the local-only tail rather than dropping it, so a
//     parallel append is never destroyed. Ordering is the cost - the recovered
//     turns sit after the server's rather than interleaved by time - and it is
//     the right trade against losing them outright. With no local tail (or no
//     local body at all) this degrades to a plain adopt.
//
// `allowAdoptServer` gates the second half. It is true only at mount, where
// nothing is rendering the chat yet. Mid-session the local body is on screen and
// the live React state would silently disagree with the IDB record we just
// rewrote - then re-save over it at the next boundary. Genuine divergence keeps
// its existing answer there: the "updated elsewhere - reload" banner, which
// resolves through the mount path.
//
// Returns the realigned entry, or null when nothing was resolved (fetch failed,
// or divergence with adoption withheld).
async function resolveConflictedChat(tenantId, localId, entry, { allowAdoptServer }) {
    const fetched = await fetchServerTranscript(entry.serverChatId, localId);
    if (!fetched) return null; // couldn't fetch - leave parked
    const localBody = await loadChat(tenantId, localId);
    if (planConflictKeepLocal(localBody, fetched.payload)) {
        return await realignToServer(tenantId, localId, entry, localBody, fetched);
    }
    if (!allowAdoptServer) return null;
    const tail = divergentLocalTail(localBody, fetched.payload);
    if (tail.length) {
        // Merge, don't replace.
        const serverItems = fetched.payload.items || [];
        // Normalize the tail to RENDER documents before splicing. The local
        // items come from the pooled v2 record, where a completed turn's
        // `snapshot` is an index array (`snapshotRef`) into THAT record's
        // `messagePool` - and the server payload they are being spliced onto
        // carries an empty pool. A spliced-in ref therefore dangles:
        // `expandPooledChatRecord` resolves it against the empty pool and
        // produces `snapshot: []`, which fork/rewind read as present (`!!node.
        // snapshot` is true for an empty array) and assign straight over
        // `agent.state.messages` - silently emptying the forked chat's context
        // instead of refusing. Going through `buildTurns` applies the same strip
        // the sync path already uses, so a recovered turn ends up shaped exactly
        // like the server's own, which never carried a snapshot either - and
        // Fork/Rewind are then correctly DISABLED on it rather than silently
        // wrong. Re-pooling the tail's snapshots into the merged record would
        // keep those affordances alive instead, at the cost of carrying pooled
        // state into a payload the restore path builds unpooled; that belongs
        // with the still-open fork-vs-prompt question, not ahead of it.
        const recovered = reidMergedTail(
            buildTurns({ items: tail }).map((turn) => turn.payload),
            serverItems,
        );
        const merged = {
            ...fetched.payload,
            items: [...serverItems, ...recovered],
        };
        await saveChat(tenantId, localId, merged);
        return await realignToServer(tenantId, localId, entry, merged, fetched);
    }
    // Nothing local to preserve -> adopt the server copy outright.
    await saveChat(tenantId, localId, fetched.payload);
    await seedRestoredOutbox(tenantId, localId, fetched.payload, fetched.head);
    return await getOutboxEntry(tenantId, localId);
}

// Resolve conflict-parked chats on reload, BEFORE drainOutbox. Both halves of
// the decision are available here (see resolveConflictedChat); a chat whose
// server fetch fails stays parked for the next reload.
export async function resolveConflicts({ tenantId }) {
    if (!chatPersistenceEnabled()) return { resolved: 0 };
    const entries = await loadOutbox(tenantId);
    let resolved = 0;
    for (const e of entries) {
        if (!e.conflict || !e.serverChatId) continue;
        const next = await resolveConflictedChat(tenantId, e.localId, e, { allowAdoptServer: true });
        if (next) resolved++;
    }
    return { resolved };
}

// App-open + periodic drain: resync every dirty chat in the outbox. Loads each
// chat body from IDB (the render items are all the PG turns need) and syncs it.
// Best-effort and bounded — one pass, no hot-loop.
export async function drainOutbox({ tenantId }) {
    if (!chatPersistenceEnabled()) return { attempted: 0, ok: 0 };
    const entries = await loadOutbox(tenantId);
    let attempted = 0;
    let ok = 0;
    for (const raw of entries) {
        const entry = resetForSession(raw, { now: now() });
        if (raw !== entry) await putOutboxEntry(tenantId, entry.localId, entry);
        if (!shouldAttempt(entry, { now: now() })) continue;
        const stored = await loadChat(tenantId, entry.localId);
        if (!stored) continue;
        attempted++;
        const result = await syncStoredChat({
            tenantId,
            localId: entry.localId,
            serverChatId: entry.serverChatId || stored.serverChatId,
            storedPayload: stored,
            // Carry the interruption marker the boundary flush would have sent, so
            // a chat first pushed by the drain (its exit flush structurally
            // skipped) still records the interrupted head flag for a fresh-device
            // restore. Read from the persisted items rather than `busyAtClose`,
            // which is always false by the time a reopened tab drains — that is
            // the common case here, since an abrupt close is exactly when the
            // flush fails to land.
            interrupted: wasInterrupted(stored),
        });
        if (result.status === "ok") ok++;
    }
    return { attempted, ok };
}

// Delete a chat everywhere: tombstone on the server (so other devices drop it)
// then locally. Best-effort on the server half — a failure there still removes
// the local copy.
export async function deleteChatEverywhere({ tenantId, localId, serverChatId }) {
    // Let any boundary sync already in flight for this chat finish first, so its
    // server upsert lands BEFORE the tombstone below (tombstone = last write
    // wins) rather than a late upsert un-deleting the chat server-side.
    await (_inFlight.get(`${tenantId}:${localId}`) || Promise.resolve());
    if (chatPersistenceEnabled() && serverChatId) {
        try {
            await deleteChatOnServer(serverChatId);
            broadcastChatDeleted({ serverChatId, localId });
        } catch (err) {
            console.warn("[chatSync] server delete failed (local delete still proceeds):", err?.message || err);
        }
    }
    await deleteChatLocal(tenantId, localId);
    // Leave a terminal 'deleted' marker instead of dropping the outbox row: a
    // boundary already queued on the in-flight lock for this chat would else
    // re-read an absent entry, fall to initialOutbox, and POST a fresh create
    // (expected_revision 0) over the server tombstone. deleteChatLocal clears
    // the chatSync row, so re-put the marker after it; shouldAttempt then
    // short-circuits the queued call. Mirrors the deleted-elsewhere path.
    await putOutboxEntry(tenantId, localId, {
        ...initialOutbox({ serverChatId: serverChatId || null, now: now() }),
        dirty: false,
        terminal: true,
        terminalKind: "deleted",
    });
}
